import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Flex,
  Image,
  List,
  ListItem,
  Text,
  Button,
  IconButton,
  Spinner,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../utils/reduxHooks';
import { getPartsAction } from '../../../modules/catalog/actions';
import {
  selectIsPartsLoading,
  selectIsSchemeLoading,
  selectParts,
  selectScheme,
} from '../../../modules/catalog/selectors';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { routes } from '../../../constants/routes';

interface Label {
  id: string;
  name: string;
  coordinate: {
    top: { x: number; y: number };
    width: number;
    height: number;
  };
}

export const Scheme = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { type, mark, model, modification, group, parentId } = useParams();
  const [highlightedLabel, setHighlightedLabel] = useState<string | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const schemeImg = useAppSelector(selectScheme);
  const parts = useAppSelector(selectParts);
  const partsLoading = useAppSelector(selectIsPartsLoading);
  const isLoadingScheme = useAppSelector(selectIsSchemeLoading);

  useEffect(() => {
    dispatch(getPartsAction.request({ type, mark, model, modification, group, parentId }));
  }, [dispatch]);

  const getScaleFactor = () => {
    if (imageRef.current) {
      const originalWidth = imageRef.current.naturalWidth;
      const displayedWidth = imageRef.current.clientWidth;
      const originalHeight = imageRef.current.naturalHeight;
      const displayedHeight = imageRef.current.clientHeight;

      return { scaleX: displayedWidth / originalWidth, scaleY: displayedHeight / originalHeight };
    }
    return { scaleX: 1, scaleY: 1 };
  };

  const filterUniqueLabels = (labels: Label[]): Label[] => {
    const seen = new Set();
    return labels.filter(label => {
      const isDuplicate = seen.has(label.id);
      seen.add(label.id);
      return !isDuplicate;
    });
  };

  const handleGoToProductPage = () => {
    navigate(
      generatePath(routes.CATALOG_FILTERS, {
        type,
        mark,
        model,
        modification,
        group,
        parentId,
        searchText: encodeURIComponent(parts?.group?.name || ''),
      }),
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box>
      <Flex
        justifyContent="space-between"
        backgroundColor="gray.100"
        mb={5}
        w="100%"
        alignItems="center"
      >
        <Flex alignItems="center" onClick={handleBack} cursor="pointer">
          <IconButton aria-label="Back" icon={<ChevronLeftIcon boxSize="32px" />} variant="ghost" />
          <Text size="lg" ml={2}>
            Назад
          </Text>
        </Flex>
        <Button border="1px solid gray.100" size="md" onClick={handleGoToProductPage}>
          Узнать цену
        </Button>
      </Flex>

      <Flex>
        <Box width="30%" padding="16px" borderRightWidth="1px">
          {/* Показать спиннер во время загрузки частей */}
          {partsLoading ? (
            <Flex justifyContent="center" alignItems="center" height="100%">
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : (
            <List spacing={2}>
              {parts &&
                filterUniqueLabels(parts.labels).map((label: Label) => (
                  <ListItem
                    key={label.id}
                    onMouseEnter={() => setHighlightedLabel(label.id)}
                    onMouseLeave={() => setHighlightedLabel(null)}
                    cursor="pointer"
                  >
                    <Box _hover={{ backgroundColor: 'gray.100' }} borderRadius="10px" p={1}>
                      <Text
                        fontWeight={highlightedLabel === label.id ? 'bold' : 'normal'}
                        color={highlightedLabel === label.id ? 'blue.500' : 'black'}
                      >
                        {label.name}
                      </Text>
                    </Box>
                  </ListItem>
                ))}
            </List>
          )}
        </Box>

        <Box flex="1" padding="16px">
          <Box width="600px" height="800px" position="relative">
            {isLoadingScheme ? (
              <Flex justifyContent="center" alignItems="center" height="100%">
                <Spinner size="xl" color="blue.500" />
              </Flex>
            ) : schemeImg ? (
              <Image src={schemeImg} alt="Scheme" style={{ width: '100%' }} ref={imageRef} />
            ) : (
              <Text>Загрузка изображения...</Text>
            )}

            {/* Наложение на изображение с учетом масштабирования */}
            {schemeImg &&
              parts &&
              filterUniqueLabels(parts.labels).map((label: Label) => {
                const { scaleX, scaleY } = getScaleFactor();
                const adjustedTop = label.coordinate.top.y * scaleY + 9;
                const adjustedLeft = label.coordinate.top.x * scaleX + 8;

                return (
                  <Box
                    key={label.id}
                    position="absolute"
                    top={`${adjustedTop}px`}
                    left={`${adjustedLeft}px`}
                    width={`${label.coordinate.width * scaleX + 10}px`}
                    height={`${label.coordinate.height * scaleY + 10}px`}
                    backgroundColor={
                      highlightedLabel === label.id ? 'rgba(0, 0, 255, 0.5)' : 'transparent'
                    }
                    border={highlightedLabel === label.id ? '2px solid blue' : 'none'}
                    pointerEvents="none"
                    transform="translate(-50%, -50%)"
                  />
                );
              })}
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};
